import axios from '@hw-core/libs/axios'
import { db, functions, auth } from '@/shared/services/firebase'
import i18n from '@hw-core/libs/i18n'
import { capitalize } from '@hw-core/utils'
import firebase from 'firebase/app'
import { 
  fetchItems,
  buildEqualCondition, buildGreaterOrEqualCondition, buildSmallerOrEqualCondition, buildLimit } from '@/shared/services/collection'
import { DB_NAMES } from '@/shared/constants/collections'
import { SHIFT_STATUSES, SHIFT_BOOST_TYPES } from '@/shared/constants/shifts'

const buildFetchLocationConditions = (accountId, startcode, endcode) => [
  buildEqualCondition('accountId', accountId),
  buildGreaterOrEqualCondition('name', startcode),
  buildSmallerOrEqualCondition('name', endcode)
]

function getStatus(item) {
  const today = new Date()
  let { status } = item
  if (item.end < today && item.status !== 'confirmed') {
    /* if (item.status === 'booked') {
      status = 'unconfirmed'
    } */
  }
  if (item.end > today && item.status === 'booked' && item.reserved) {
    status = 'verified'
  }
  return capitalize(status)
}
export default {
  namespaced: true,
  state: {
    calendarOptions: [
      {
        color: 'info',
        label: i18n.t('shifts_calendar.open'),
        value: 'Open',
      },
      {
        color: 'purple',
        label: i18n.t('draft'),
        value: 'Draft',
      },
      {
        color: 'warning',
        label: i18n.t('shifts_calendar.booked'),
        value: 'Booked',
      },
      {
        color: 'done',
        label: i18n.t('shifts_calendar.verified'),
        value: 'Verified',
      },
      {
        color: 'danger',
        label: i18n.t('shifts_calendar.expired'),
        value: 'Expired',
      },
      {
        color: 'success',
        label: i18n.t('shifts_calendar.confirmed'),
        value: 'Confirmed',
      },
    ],
    selectedCalendars: ['Booked', 'Verified', 'Expired', 'Confirmed'],
  },
  getters: {},
  mutations: {
    SET_SELECTED_EVENTS(state, val) {
      state.selectedCalendars = val
    },
  },
  actions: {
    async fetchLocation(ctx, { text, accountId }) {
      const strlength = text.length
      const strFrontCode = text.slice(0, strlength - 1)
      const strEndCode = text.slice(strlength - 1, strlength)
      const startcode = text
      const endcode = strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1)
      try {
        const conditions = buildFetchLocationConditions(accountId, startcode, endcode)
        const pagination = buildLimit(6)

        const areas = await fetchItems(DB_NAMES.AREAS, conditions, null, pagination)

        return areas
      } catch (error) {
        return Promise.reject(error)
      }
    },
    fetchEvents(ctx, {
      info, calendars, accountId, onlyAreas, areaId,
    }) {
      return new Promise((resolve, reject) => {
        const states = calendars.map(s => s.toLowerCase())
        let query = db.collection('shifts')
          .where('accountId', '==', accountId)
          .where('start', '>=', info.start)
          .where('start', '<=', info.end)
        if (states.length > 0) {
          query = query.where('status', 'in', states)
        } else {
          query = query.where('status', 'in', [''])
        }
        if (areaId !== null) {
          query = query.where('areaId', '==', areaId)
        }
        query
          .limit(1500)
          .get()
          .then(async snapshot => {
            let data = snapshot.docs.map(doc => {
              const item = doc.data()
              item.id = doc.id
              item.start = item.start.toDate()
              item.end = item.end.toDate()
              item.extendedProps = {
                calendar: getStatus(item),
                isDraft: item.isDraft,
                gigId: item.gigId,
                areaId: item.areaId,
                accountId: item.accountId,
                assignedTo: item.assignedTo,
                requirements: item.requirements ? item.requirements : null,
                kilometrikorvaus: item.kilometrikorvaus ? item.kilometrikorvaus : 0,
                workDayType: item.workDayType ? item.workDayType : '',
                updates: item.updates ? item.updates : [],
                boosted: item.boosted ? item.boosted : false,
                boostedOptions: item.boostedOptions,
              }
              item.url = ''
              return item
            })
            if (onlyAreas) {
              data = data.filter(item => onlyAreas.indexOf(item.areaId) > -1)
            }
            resolve(data)
          })
          .catch(() => {
            reject()
          })
      })
    },
    searchUsers(ctx, { q }) {
      return new Promise((resolve, reject) => {
        const strlength = q.length
        const strFrontCode = q.slice(0, strlength - 1)
        const strEndCode = q.slice(strlength - 1, strlength)
        const startcode = q
        const endcode = strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1)
        db.collection('users')
          .where('isAdmin', '==', false)
          .where('confirmed', '==', true)
          .where('fullName', '>=', startcode)
          .where('fullName', '<=', endcode)
          .limit(6)
          .get()
          .then(async snapshot => {
            const data = snapshot.docs.map(doc => {
              const item = doc.data()
              item.id = doc.id
              return item
            })
            resolve(data)
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    fetchLocationDetails(ctx, { id }) {
      return new Promise((resolve, reject) => {
        db.collection('areas').doc(id)
          .get()
          .then(async doc => {
            if (!doc.exists) {
              reject()
            } else {
              // Get account name
              const account = await db.collection('accounts').doc(doc.data().accountId).get()
              if(!account.exists) {
                resolve({ ...doc.data(), id: doc.id, accountName: '' })
                return
              }
              resolve({ ...doc.data(), areaId: doc.id, accountName: account.data().name })
            }
          })
          .catch(() => {
            reject()
          })
      })
    },
    fetchUserDetails(ctx, { assignedTo }) {
      return new Promise((resolve, reject) => {
        db.collection('users').doc(assignedTo)
          .get()
          .then(doc => {
            if (!doc.exists) {
              reject()
            } else {
              resolve(doc.data())
            }
          })
          .catch(() => {
            reject()
          })
      })
    },
    addEvent(ctx, { event }) {
      return new Promise((resolve, reject) => {
        axios
          .post('/apps/calendar/events', { event })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    verifyBooking(ctx, { to, message }) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${functions.url}/sendBookingVerification`, { to, message })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    sendEmail(ctx, { to, message, subject }) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${functions.url}/sendEmail`, { to, message, subject })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    sendSMS(ctx, { mobile, message }) {
      return new Promise((resolve, reject) => {
        auth.currentUser.getIdToken(true).then(idToken => {
          axios
            .post(`${functions.url}/sendSMSMessage`, { to: mobile, message, token: idToken })
            .then(response => resolve(response))
            .catch(error => reject(error))
        }).catch(error => {
          reject(error)
        })
      })
    },
    updateShift(ctx, { shift }) {
      return new Promise((resolve, reject) => {
        const isPublished = !shift.extendedProps.isDraft
        // eslint-disable-next-line no-nested-ternary
        let status = (shift.extendedProps.assignedTo && shift.extendedProps.assignedTo.length > 0)
          ? 'booked'
          : isPublished
            ? 'open'
            : 'draft'
        let {boosted, boostedOptions} = shift.extendedProps

        if (shift.status && shift.status !== 'draft' && shift.status !== 'open') {
          status = shift.status
        }
        if (status === SHIFT_STATUSES.expired && shift.extendedProps.boostedOptions.boostType === SHIFT_BOOST_TYPES.quick.value) {
          boosted = false
          boostedOptions = {}
        }
        db.collection('shifts').doc(shift.id).get()
          .then(res => {
            db.collection('shifts').doc(shift.id).update({
              title: shift.title,
              startStr: shift.startStr,
              endStr: shift.endStr,
              start: shift.start.seconds === undefined ? new Date(shift.start) : res.data().start.toDate(),
              end: shift.end.seconds === undefined ? new Date(shift.end) : res.data().end.toDate(),
              kilometrikorvaus: shift.extendedProps.kilometrikorvaus,
              workDayType: shift.extendedProps.workDayType ? shift.extendedProps.workDayType : 'work',
              reserved: (shift.extendedProps.reserved && shift.extendedProps.assignedTo.length > 2) ? shift.extendedProps.reserved : false,
              assignedTo: shift.extendedProps.assignedTo,
              status,
              updates: shift.extendedProps.updates,
              boosted,
              boostedOptions,
              areaId: shift.areaId === undefined ? res.data().areaId : shift.areaId,
              contractId: shift.contractId === undefined ? res.data().contractId : shift.contractId,
            })
              .then(() => {
                resolve()
              })
              .catch(() => {
                reject()
              })
          })
      })
    },
    createGigAndUpdateShift(ctx, { shift }) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          const gigDoc = await db.collection('gigs').doc(shift.extendedProps.gigId).get()
          const oldGig = {
            ...gigDoc.data(),
            id: gigDoc.id,
          }

          // Create new gig
          const newGigId = (await db.collection('gigs').add({
            ...oldGig,
            isPublished: true,
            title: `1 vuoro: ${shift.title}`,
            availableShifts: 1,
            totalShifts: 1,
            boosted: shift.extendedProps.boosted ? shift.extendedProps.boosted : false,
            boostedOptions: shift.extendedProps.boostedOptions ? shift.extendedProps.boostedOptions : {},
            startDate: shift.start,
            expDate: oldGig.expDate.seconds === oldGig.endDate.seconds
              ? shift.start
              : oldGig.expDate,
            endDate: shift.start,
          })).id

          // Update shift
          await db.collection('shifts').doc(shift.id).update({
            title: shift.title,
            startStr: shift.startStr,
            endStr: shift.endStr,
            start: new Date(shift.start),
            end: new Date(shift.end),
            kilometrikorvaus: shift.extendedProps.kilometrikorvaus,
            workDayType: shift.extendedProps.workDayType ? shift.extendedProps.workDayType : 'work',
            reserved: (shift.extendedProps.reserved && shift.extendedProps.assignedTo.length > 2) ? shift.extendedProps.reserved : false,
            assignedTo: '',
            status: 'open',
            isDraft: false,
            updates: shift.extendedProps.updates,
            boosted: shift.extendedProps.boosted ? shift.extendedProps.boosted : false,
            boostedOptions: shift.extendedProps.boostedOptions ? shift.extendedProps.boostedOptions : {},
            gigId: newGigId,
          })

          const shiftsCollection = await db.collection('shifts').where('gigId', '==', oldGig.id).get()
          const oldShiftGigs = shiftsCollection.docs.map(oldShift => ({
            ...oldShift.data(),
            id: oldShift.id,
          })).sort((a, b) => a.start < b.start)

          // Update old gig
          await gigDoc.ref.update({
            availableShifts: oldGig.availableShifts - 1,
            startDate: oldShiftGigs[0].start,
            endDate: oldShiftGigs[oldShiftGigs.length - 1].start,
            expDate: oldGig.endDate.seconds === oldGig.expDate.seconds
              ? oldShiftGigs[oldShiftGigs.length - 1].start
              : oldGig.expDate,
            totalShifts: oldGig.totalShifts - 1,
            title: `${oldGig.availableShifts - 1} ${oldGig.availableShifts - 1 > 1 ? 'vuoroa' : 'vuoro'}: ${shift.title}`,
          })

          resolve()
        } catch (error) {
          reject()
        }
      })
    },
    publishGig(ctx, { gigId }) {
      return new Promise((resolve, reject) => {
          // Get all shifts belong to this gig and update their status to open if they are draft
        db.collection('shifts')
          .where('gigId', '==', gigId)
          .where('status', '==', 'draft')
          .get()
          .then(async snapshot => {
            const data = snapshot.docs.map(doc => {
              const item = doc.data()
              item.id = doc.id
              return item
            })
            await Promise.all(data.map(async item => {
              await db.collection('shifts').doc(item.id).update({
                status: 'open',
                isDraft: false,
              })
            }))
            db.collection('gigs').doc(gigId).update({
              isPublished: true,
            }).then(() => {
              resolve()
            }
            ).catch(() => {
              reject()
            })
          })
          .catch(() => {
            reject()
          }
          )
      })
    },
    deleteShift(ctx, { id, processer }) {
      return new Promise((resolve, reject) => {
        db.collection('shifts').doc(id).update({
          status: 'deleted',
          deletedAt: new Date(),
          updates: firebase.firestore.FieldValue.arrayUnion({
            state: 'deleted',
            date: new Date(),
            processer: `${processer.firstName} ${processer.lastName}`,
            content: [
              "Vuoro poistettu",
            ]
          }),
        })
          .then(() => {
            resolve()
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    fetchUserUpcomingShifts(ctx, { assignedTo }) {
      return new Promise((resolve, reject) => {
        db.collection('shifts')
          .where('assignedTo', '==', assignedTo)
          .where('status', '==', 'booked')
          .where('end', '>=', new Date())
          .orderBy('end', 'asc')
          .limit(900)
          .get()
          .then(async snapshot => {
            const data = snapshot.docs.map(doc => {
              const item = doc.data()
              return {
                ...item, id: doc.id, start: item.start.toDate(), end: item.end.toDate(),
              }
            })
            resolve(data)
          })
          .catch(() => {
            reject()
          })
      })
    },
    updateUser(ctx, { id, data }) {
      return new Promise((resolve, reject) => {
        db.collection('users').doc(id).update(data)
          .then(() => {
            resolve()
          })
          .catch(err => {
            reject(err)
          })
      })
    },
  },
}
